import React, { useState, useRef, useEffect } from 'react';

export default function AccountInput({ accounts, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const wrapperRef = useRef();
  const inputRef = useRef();
  const listRef = useRef();

  const selected = accounts.find(a => String(a.id) === String(value));

  const filtered = query
    ? accounts.filter(a =>
        String(a.account_number).startsWith(query) ||
        a.name.toLowerCase().includes(query.toLowerCase())
      )
    : accounts;

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  useEffect(() => {
    if (open && listRef.current) {
      const el = listRef.current.children[highlighted];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectAccount = (account) => {
    onChange(String(account.id));
    setQuery('');
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    setQuery('');
    setHighlighted(0);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      setOpen(true);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[highlighted]) {
        selectAccount(filtered[highlighted]);
      }
    } else if (e.key === 'Tab') {
      if (filtered.length === 1) {
        selectAccount(filtered[0]);
      } else if (query && filtered[highlighted]) {
        selectAccount(filtered[highlighted]);
      }
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  const handleChange = (e) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const displayValue = open ? query : (selected ? `${selected.account_number} - ${selected.name}` : '');

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className="form-control"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || 'Skriv kontonr...'}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div ref={listRef} style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'white', border: '1px solid var(--gray-300)',
          borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto',
          zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}>
          {filtered.map((a, i) => (
            <div
              key={a.id}
              onMouseDown={(e) => { e.preventDefault(); selectAccount(a); }}
              style={{
                padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem',
                background: i === highlighted ? 'var(--primary)' : 'white',
                color: i === highlighted ? 'white' : 'inherit'
              }}
            >
              <strong>{a.account_number}</strong> - {a.name}
            </div>
          ))}
        </div>
      )}
      {open && query && filtered.length === 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: 'white', border: '1px solid var(--gray-300)',
          borderRadius: '0 0 6px 6px', padding: '0.5rem',
          zIndex: 100, fontSize: '0.85rem', color: 'var(--gray-500)'
        }}>
          Ingen konti fundet
        </div>
      )}
    </div>
  );
}
